import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Search, Check } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { TeamPermissionsSection } from '@/components/admin/TeamPermissionsEditor';
import {
  GHL_LIST_USERS_ERROR_COPY,
  buildFromGhlSubmitBody,
  type GhlUserLite,
} from '@/pages/admin/RolesPermissions';

/**
 * Phase 16.2-06 — EditTeamMemberModal.
 *
 * Single-transaction edit flow for a team member's role, display name, GHL
 * exact name, and permissions. Routes all writes through the
 * `update-team-member` edge fn (Plan 16.2-04), which handles the 9-row
 * transitions table + idempotency + audit-log server-side.
 *
 * Sections (top to bottom):
 *   1. Role — Select of 5 role options with helper description.
 *       Warning banner shown on setter↔closer swap.
 *   2. Name — display_name (always) + GHL exact name (setter/closer only).
 *   3. Permissions — TeamPermissionsSection in 'pending' mode (setter/closer only).
 */

export type TeamMemberRole =
  | 'super_admin'
  | 'team_admin'
  | 'member'
  | 'setter'
  | 'closer';

export interface EditTeamMemberModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  member: {
    user_id: string;
    email: string;
    full_name: string;
    role: TeamMemberRole;
    // Only meaningful when current role is setter/closer — source roster row's name.
    linked_roster_name?: string;
  };
  onSaved?: () => void;
}

const ROLE_HELPERS: Record<TeamMemberRole, string> = {
  super_admin: 'Full access including billing and team management',
  team_admin: 'Manage clients and team but not billing',
  member: 'Read-only access to limited pages',
  setter: 'Attribution roster — sets demos; can be linked to a login',
  closer: 'Attribution roster — closes demos; can be linked to a login',
};

function isTeamRole(r: TeamMemberRole): r is 'setter' | 'closer' {
  return r === 'setter' || r === 'closer';
}

export function EditTeamMemberModal({
  open,
  onOpenChange,
  member,
  onSaved,
}: EditTeamMemberModalProps) {
  const [newRole, setNewRole] = useState<TeamMemberRole>(member.role);
  const [newDisplayName, setNewDisplayName] = useState<string>(member.full_name);
  const [newGhlName, setNewGhlName] = useState<string>(
    member.linked_roster_name ?? ''
  );
  const [pendingPermChanges, setPendingPermChanges] = useState<
    Record<string, boolean>
  >({});
  const [saving, setSaving] = useState(false);

  // Phase 16.3-01 — GHL picker state (optional re-bind to a GHL identity).
  const [pickerOpen, setPickerOpen] = useState(false);
  const [activeClients, setActiveClients] = useState<
    Array<{ id: string; company_name: string }>
  >([]);
  const [selectedGhlClientId, setSelectedGhlClientId] = useState<string | null>(
    null
  );
  const [ghlUsers, setGhlUsers] = useState<GhlUserLite[] | null>(null);
  const [ghlUsersLoading, setGhlUsersLoading] = useState(false);
  const [selectedGhlUser, setSelectedGhlUser] = useState<GhlUserLite | null>(
    null
  );
  const [ghlError, setGhlError] = useState<string | null>(null);
  // Phase 16.5 — tracks the exact GHL name assigned via the picker, so we can
  // show a "✓ Synced from GoHighLevel" hint while the Input value still matches
  // the picked name (cleared the instant the admin edits manually).
  const [lastPickedGhlName, setLastPickedGhlName] = useState<string | null>(null);

  // Reinit state on open from member prop.
  useEffect(() => {
    if (open) {
      setNewRole(member.role);
      setNewDisplayName(member.full_name);
      setNewGhlName(member.linked_roster_name ?? '');
      setPendingPermChanges({});
      // Reset GHL picker state on open.
      setPickerOpen(false);
      setActiveClients([]);
      setSelectedGhlClientId(null);
      setGhlUsers(null);
      setGhlUsersLoading(false);
      setSelectedGhlUser(null);
      setGhlError(null);
      setLastPickedGhlName(null);
    }
  }, [
    open,
    member.user_id,
    member.role,
    member.full_name,
    member.linked_roster_name,
  ]);

  // Phase 16.3-01 — lazy-load active clients on first picker open.
  useEffect(() => {
    if (!open) return;
    if (!pickerOpen) return;
    if (activeClients.length > 0) return;
    supabase
      .from('clients')
      .select('id, company_name')
      .eq('status', 'active')
      .order('company_name')
      .then(({ data }) => setActiveClients((data as any) || []));
  }, [open, pickerOpen, activeClients.length]);

  // Phase 16.3-01 — fetch GHL users when a client is selected.
  useEffect(() => {
    if (!open || !pickerOpen) return;
    if (!selectedGhlClientId) return;
    setGhlUsersLoading(true);
    setGhlError(null);
    setGhlUsers(null);
    setSelectedGhlUser(null);
    (async () => {
      try {
        const { data, error } = await supabase.functions.invoke('ghl-list-users', {
          body: { client_id: selectedGhlClientId },
        });
        const code: string | undefined =
          (data && (data as any).error) ||
          (error && ((error as any).error_code));
        if (code && GHL_LIST_USERS_ERROR_COPY[code]) {
          const msg = GHL_LIST_USERS_ERROR_COPY[code];
          setGhlError(msg);
          toast.error(msg);
          setGhlUsers([]);
          return;
        }
        if (error) {
          const msg = GHL_LIST_USERS_ERROR_COPY.ghl_unavailable;
          setGhlError(msg);
          toast.error(msg);
          setGhlUsers([]);
          return;
        }
        const users: GhlUserLite[] = ((data as any)?.users || []) as GhlUserLite[];
        if (users.length === 0) {
          const msg = 'No users found in this GoHighLevel account';
          setGhlError(msg);
          toast.error(msg);
        }
        setGhlUsers(users);
      } catch {
        const msg = GHL_LIST_USERS_ERROR_COPY.ghl_unavailable;
        setGhlError(msg);
        toast.error(msg);
        setGhlUsers([]);
      } finally {
        setGhlUsersLoading(false);
      }
    })();
  }, [open, pickerOpen, selectedGhlClientId]);

  const handlePermChange = (key: string, enabled: boolean) => {
    setPendingPermChanges((prev) => ({ ...prev, [key]: enabled }));
  };

  const showGhlName = isTeamRole(member.role) || isTeamRole(newRole);
  const showPermissions = isTeamRole(newRole);
  const showSetterCloserWarning =
    isTeamRole(member.role) &&
    isTeamRole(newRole) &&
    member.role !== newRole;

  const warningText = showSetterCloserWarning
    ? `Their attribution roster row will be moved from ${
        member.role === 'setter' ? 'setters' : 'closers'
      } to ${newRole === 'setter' ? 'setters' : 'closers'} — past attribution data is preserved`
    : '';

  const handleSave = async () => {
    setSaving(true);
    try {
      // Phase 16.3-01 — if a GHL identity was picked, run the idempotency lookup.
      // If the helper returns a body with link_closer_id / link_vh_team_member_id,
      // surface a conflict warning (this edit flow is re-binding the CURRENT user;
      // linking to a different existing roster row would create a duplicate roster row
      // for this user_id — we want a clean rename/re-bind, not a merge).
      if (selectedGhlUser && isTeamRole(newRole)) {
        try {
          const idempotencyBody = await buildFromGhlSubmitBody(
            newRole as 'setter' | 'closer',
            selectedGhlUser,
            false
          );
          if (
            (idempotencyBody as any).link_closer_id ||
            (idempotencyBody as any).link_vh_team_member_id
          ) {
            toast.error(
              'This name conflicts with another roster row — pick a different person or rename them in GHL'
            );
            setSaving(false);
            return;
          }
        } catch {
          // Non-blocking — if the idempotency lookup itself errors, fall through
          // to the server-side name_collision check in update-team-member.
        }
      }

      const body: Record<string, unknown> = { user_id: member.user_id };
      if (newRole !== member.role) body.new_role = newRole;
      if (newDisplayName !== member.full_name)
        body.new_display_name = newDisplayName;
      if (
        showGhlName &&
        newGhlName &&
        newGhlName !== (member.linked_roster_name ?? '')
      ) {
        body.new_ghl_name = newGhlName;
      }
      if (Object.keys(pendingPermChanges).length > 0) {
        body.permission_updates = pendingPermChanges;
      }

      const { data, error } = await supabase.functions.invoke(
        'update-team-member',
        { body }
      );

      // Error mapping — inline at the invoke call site.
      const errCode: string | undefined =
        (data && (data as any).error) ||
        (error && ((error as any).error_code || (error as any).message));
      const status: number | undefined =
        (error && (error as any).context && (error as any).context.status) ||
        (error && (error as any).status);

      if (error || (data && (data as any).error)) {
        if (errCode === 'name_collision' || status === 409) {
          toast.error('That name is already taken by another team member.');
        } else if (
          errCode === 'transition_failed_rolled_back' ||
          status === 500
        ) {
          toast.error('Update failed — no changes were applied. Try again.');
        } else if (errCode === 'user_not_found' || status === 404) {
          toast.error('Team member not found.');
        } else {
          toast.error(
            (data as any)?.error ?? (error as any)?.message ?? 'Unexpected error'
          );
        }
        setSaving(false);
        return;
      }

      toast.success('Team member updated');
      onSaved?.();
      onOpenChange(false);
    } catch (err: any) {
      toast.error(err?.message || 'Unexpected error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-surface border-border max-w-2xl max-h-[90vh] flex flex-col p-0 gap-0">
        <DialogHeader className="shrink-0 px-6 pt-6 pb-3 border-b border-border/50">
          <DialogTitle>Edit Team Member</DialogTitle>
          <DialogDescription>
            Update role, name, and per-person permissions. All changes are
            applied atomically.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
          {/* Role section */}
          <div className="space-y-2">
            <Label htmlFor="edit-role">Role</Label>
            <Select
              value={newRole}
              onValueChange={(v: TeamMemberRole) => setNewRole(v)}
            >
              <SelectTrigger id="edit-role">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="super_admin">Super Admin</SelectItem>
                <SelectItem value="team_admin">Team Admin</SelectItem>
                <SelectItem value="member">Member</SelectItem>
                <SelectItem value="setter">Setter</SelectItem>
                <SelectItem value="closer">Closer</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {ROLE_HELPERS[newRole]}
            </p>

            {showSetterCloserWarning && (
              <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-200">
                {warningText}
              </div>
            )}
          </div>

          <div className="border-t border-border" />

          {/* Name section */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-display-name">Display name</Label>
              <Input
                id="edit-display-name"
                value={newDisplayName}
                onChange={(e) => setNewDisplayName(e.target.value)}
                placeholder="Jane Doe"
              />
            </div>

            {showGhlName && (
              <div className="space-y-2">
                <Label htmlFor="edit-ghl-name">GHL exact name</Label>
                {/* Phase 16.5 — Input + inline helper button in a single flex row. */}
                <div className="flex items-center gap-2">
                  <Input
                    id="edit-ghl-name"
                    className="flex-1"
                    value={newGhlName}
                    onChange={(e) => setNewGhlName(e.target.value)}
                    placeholder="Name as it appears in GoHighLevel"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="shrink-0"
                    onClick={() => setPickerOpen((v) => !v)}
                  >
                    <Search className="w-4 h-4 mr-1" />
                    {pickerOpen ? 'Hide' : 'Pick from GHL'}
                  </Button>
                </div>

                {/* Phase 16.5 — success hint shown only while the current Input value
                    still matches the value last assigned by the picker. */}
                {newGhlName === lastPickedGhlName && lastPickedGhlName !== null && (
                  <p className="text-xs text-emerald-400 flex items-center gap-1">
                    <Check className="w-3 h-3" />
                    Synced from GoHighLevel
                  </p>
                )}

                <p className="text-xs text-muted-foreground">
                  This must match the user's name in GoHighLevel exactly for
                  attribution to resolve.
                </p>

                {pickerOpen && (
                  <div className="space-y-3 rounded-md border border-border bg-surface-muted/40 p-3 mt-2">
                    <div className="space-y-2">
                      <Label>Client (GoHighLevel sub-account)</Label>
                      <Select
                        value={selectedGhlClientId ?? ''}
                        onValueChange={(v) => setSelectedGhlClientId(v)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Pick a client..." />
                        </SelectTrigger>
                        <SelectContent>
                          {activeClients.length === 0 ? (
                            <div className="px-2 py-1.5 text-xs text-muted-foreground">
                              No active clients found.
                            </div>
                          ) : (
                            activeClients.map((c) => (
                              <SelectItem key={c.id} value={c.id}>
                                {c.company_name}
                              </SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                    </div>

                    {selectedGhlClientId && (
                      <div className="space-y-2">
                        <Label>GoHighLevel User</Label>
                        {ghlUsersLoading ? (
                          <p className="text-xs text-muted-foreground">
                            Loading GoHighLevel users…
                          </p>
                        ) : ghlError ? (
                          <p className="text-xs text-red-400">{ghlError}</p>
                        ) : (
                          <Select
                            value={selectedGhlUser?.id ?? ''}
                            onValueChange={(v) => {
                              const picked = (ghlUsers || []).find((u) => u.id === v);
                              if (picked) {
                                setSelectedGhlUser(picked);
                                const fullName = `${picked.firstName} ${picked.lastName}`.trim();
                                setNewGhlName(fullName);
                                // Phase 16.5 — remember the picked name so we can show
                                // the "Synced from GoHighLevel" hint until the admin edits.
                                setLastPickedGhlName(fullName);
                              }
                            }}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Pick a user..." />
                            </SelectTrigger>
                            <SelectContent>
                              {(ghlUsers || []).length === 0 ? (
                                <div className="px-2 py-1.5 text-xs text-muted-foreground">
                                  No users found in this GoHighLevel account
                                </div>
                              ) : (
                                (ghlUsers || []).map((u) => (
                                  <SelectItem key={u.id} value={u.id}>
                                    {u.firstName} {u.lastName} ({u.email})
                                  </SelectItem>
                                ))
                              )}
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Permissions section (only when target role is setter/closer) */}
          {showPermissions && (
            <>
              <div className="border-t border-border" />
              <div className="space-y-2">
                <Label>Permissions</Label>
                <TeamPermissionsSection
                  userId={member.user_id}
                  role={newRole as 'setter' | 'closer'}
                  mode="pending"
                  pendingChanges={pendingPermChanges}
                  onPendingChange={handlePermChange}
                />
              </div>
            </>
          )}
        </div>

        <DialogFooter className="shrink-0 border-t border-border/50 px-6 py-3">
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button type="button" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
